import RAPIER from '@dimforge/rapier3d-compat';

import type { Vec3 } from '../math.js';
import type { CollisionMap } from './brushes.js';

/**
 * Static collision world, used for **queries only**.
 *
 * The important thing about this class: Rapier simulates nothing. `step()` is
 * called exactly once, at construction, purely to build the broad-phase (see
 * `create`). There are no dynamic bodies, no solver iterations, no contact
 * caching, and gravity is zero. Rapier answers one question — "how far can this
 * capsule travel before it hits something, and what is the surface normal" — and
 * our own code in `movement.ts` does all the integration in double precision.
 *
 * That split is deliberate, and it is what makes client-side prediction viable:
 *
 * - Rapier's own `KinematicCharacterController` is convenient but its results
 *   depend on internal f32 solver ordering and cross-frame state. Two machines
 *   running it can disagree in ways that are impossible to reconcile cheaply.
 * - A stateless geometric query against static geometry is far more stable.
 *   Every frame starts from scratch, so there is no accumulated divergence.
 *
 * Rapier is still f32 internally, so agreement is close but not bit-exact across
 * architectures. That is fine and intended: the server stays authoritative and
 * the client reconciles. The requirement is that corrections are *rare*, not that
 * they never happen (ADR-0001 budgets <2% of frames).
 */

let rapierReady: Promise<void> | null = null;

/**
 * Loads the Rapier WASM module once per process. Safe to call repeatedly.
 *
 * Emits "using deprecated parameters for the initialization function" on 0.19.3 —
 * that warning comes from inside rapier3d-compat's own wasm-bindgen bootstrap, not
 * from this call. Nothing to fix on our side; noted so nobody goes hunting.
 */
export function initPhysics(): Promise<void> {
  rapierReady ??= RAPIER.init();
  return rapierReady as Promise<void>;
}

export interface SweepHit {
  /** Fraction of the requested motion travelled before contact, 0..1. */
  fraction: number;
  /** Surface normal at the contact, pointing out of the obstacle. */
  normal: Vec3;
}

export interface RayHit {
  /** Distance along the ray to the hit, in metres. */
  distance: number;
  /** Surface normal at the hit, pointing out of the obstacle. */
  normal: Vec3;
}

const IDENTITY_ROTATION = { x: 0, y: 0, z: 0, w: 1 };

/**
 * Standoff distance the controller keeps between the capsule and any surface.
 *
 * This is a *positional* gap maintained by `movement.ts` (which pushes out along
 * the contact normal after every hit), NOT a query tolerance. Passing it as
 * Rapier's `targetDistance` instead looks equivalent and is not: a grounded
 * player is within the tolerance of the floor, so every horizontal sweep returns
 * the floor at fraction 0. The move then burns all its slide iterations on
 * repeated floor contacts and the player cannot walk at all.
 */
export const COLLISION_SKIN = 0.015;

export class CollisionWorld {
  private readonly world: RAPIER.World;
  /** Capsule shapes are reused: one allocation per distinct player size, not one
   * per sweep. A move does up to 4 sweeps plus 2 step probes plus a ground probe,
   * for every player, every tick. */
  private readonly capsules = new Map<string, RAPIER.Capsule>();

  private constructor(
    readonly map: CollisionMap,
    world: RAPIER.World,
  ) {
    this.world = world;
  }

  /** `initPhysics()` must have resolved before calling this. */
  static create(map: CollisionMap): CollisionWorld {
    // Zero gravity: nothing is ever simulated, so gravity would be meaningless.
    const world = new RAPIER.World({ x: 0, y: 0, z: 0 });

    for (const brush of map.brushes) {
      const body = world.createRigidBody(
        RAPIER.RigidBodyDesc.fixed().setTranslation(brush.center.x, brush.center.y, brush.center.z),
      );
      world.createCollider(
        RAPIER.ColliderDesc.cuboid(
          brush.halfExtents.x,
          brush.halfExtents.y,
          brush.halfExtents.z,
        ),
        body,
      );
    }

    // Exactly one step, at construction, and never again.
    //
    // `castShape` queries the broad-phase acceleration structure, and in 0.19.3
    // that structure is only populated inside `step()` — there is no
    // `updateSceneQueries()` on this version. Without this call every query
    // silently returns null: no collision anywhere, players fall through the
    // floor forever, and nothing errors.
    //
    // Stepping is safe here precisely because nothing is simulated: gravity is
    // zero and every body is fixed, so this builds the BVH and integrates
    // nothing.
    world.step();

    return new CollisionWorld(map, world);
  }

  /**
   * Sweeps a capsule from `origin` along `motion` and returns the first contact.
   *
   * `origin` is the capsule's centre. `halfHeight` is half the *cylindrical*
   * section, excluding the end caps — Rapier's convention, and a common source of
   * players who are silently the wrong height.
   */
  sweepCapsule(
    origin: Vec3,
    motion: Vec3,
    radius: number,
    halfHeight: number,
  ): SweepHit | null {
    const hit = this.world.castShape(
      origin,
      IDENTITY_ROTATION,
      motion,
      this.capsule(radius, halfHeight),
      0, // targetDistance: see COLLISION_SKIN — the standoff is positional, not a tolerance
      1, // maxToi of 1 makes time_of_impact a fraction of `motion`
      true, // stopAtPenetration: report rather than tunnel if already overlapping
    );

    if (!hit) return null;

    return {
      fraction: Math.min(1, Math.max(0, hit.time_of_impact)),
      // `normal1`, verified empirically against a flat floor: it is the normal on
      // the *obstacle* (+Y for a floor), which is the one to slide along and test
      // for walkability. `normal2` is the normal on the cast capsule and points the
      // opposite way. Using it inverts every normal in the system — floors read as
      // walls, the ground probe never finds ground, and players fall through the
      // world with no error anywhere. Pinned by a test.
      normal: { x: hit.normal1.x, y: hit.normal1.y, z: hit.normal1.z },
    };
  }

  /**
   * Distance along a ray to the first level geometry hit, or null within `maxDistance`.
   *
   * This is what stops a shot at a wall. Players are *not* in this world — they are
   * tested analytically at rewound positions (`shared/src/hitscan.ts`) — so a caller
   * resolving a shot must compare this distance against its player hits and take
   * whichever is nearer. Skipping that comparison is how you get a game where people
   * shoot through walls.
   */
  castRay(origin: Vec3, direction: Vec3, maxDistance: number): number | null {
    const hit = this.world.castRay(
      new RAPIER.Ray(origin, direction),
      maxDistance,
      // solid: a ray starting inside a brush reports distance 0 rather than passing
      // through it. A player embedded in geometry should not be able to shoot out of it.
      true,
    );
    return hit ? hit.timeOfImpact : null;
  }

  /**
   * `castRay`, plus the surface normal at the hit — what the ground probe needs to tell the
   * top of a brush (standable) from its side (not). A ray that starts inside a brush reports
   * distance 0 and no usable normal; callers wanting "ground below" must reject that case.
   */
  castRayWithNormal(origin: Vec3, direction: Vec3, maxDistance: number): RayHit | null {
    const hit = this.world.castRayAndGetNormal(new RAPIER.Ray(origin, direction), maxDistance, true);
    if (!hit) return null;
    return {
      distance: hit.timeOfImpact,
      normal: { x: hit.normal.x, y: hit.normal.y, z: hit.normal.z },
    };
  }

  private capsule(radius: number, halfHeight: number): RAPIER.Capsule {
    const key = `${radius}:${halfHeight}`;
    let shape = this.capsules.get(key);
    if (!shape) {
      shape = new RAPIER.Capsule(halfHeight, radius);
      this.capsules.set(key, shape);
    }
    return shape;
  }

  free(): void {
    // Shapes created through the compat wrapper are plain JS objects with no
    // `free()`; only the world owns WASM memory.
    this.capsules.clear();
    this.world.free();
  }
}
